import React, { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CircularProgress, Divider, Typography, useTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { ReactComponent as DownloadIcon } from 'assets/downloadIcon.svg'
import ThemeButton from 'common/ui/theme-button'
import SimpleTooltip from 'common/ui/simple-tooltip'
import { type GeneratedFile, GeneratedFileType } from 'common/files/files.types'
import ThemeConfirmDialog from 'common/ui/theme-confirm-dialog'
import { formatDate, getDateRetryVerification } from 'utils/functions'
import { downloadFile, getFileUrl } from 'common/files/files.service'
import {
  getCertificateIcon,
  getValidityCertificateDate,
  useIsDatePassed,
} from 'common/certificates/certificates.functions'
import { Role } from 'common/utils/types'

import { getDecisionDetailsCompany, getVendorCompany, respondToVerification } from '../vendor.service'
import { VendorStatus } from '../vendor.types'

import DecisionMechanism from './components/decision-mechanism'
import InfoPanel from './components/info-panel'
import DeclineVerificationDialog from './decline-verification-dialog'
import styles from './VerificationProfile.module.scss'

const VerificationProfile: React.FC = (): ReactElement => {
  const { vendorId } = useParams()
  const dispatch = useAppDispatch()
  const { palette } = useTheme()

  const { vendor } = useAppSelector((state) => state.vendor)
  const [openAcceptDialog, setOpenAcceptDialog] = useState<boolean>(false)
  const [openRejectDialog, setOpenRejectDialog] = useState<boolean>(false)
  const [openRequestDocumentsDialog, setOpenRequestDocumentsDialog] = useState<boolean>(false)
  const [loadingRefDoc, setLoadingRefDoc] = useState<number | null>(null)
  const [loadingOtherDoc, setLoadingOtherDoc] = useState<number | null>(null)

  useEffect(() => {
    if (vendorId) {
      void dispatch(getVendorCompany({ vendorId }))
      void dispatch(getDecisionDetailsCompany({ vendorId }))
    }
  }, [vendorId])

  const handleCloseAcceptDialog = (): void => {
    setOpenAcceptDialog(false)
  }
  const handleCloseVerificationDialog = (): void => {
    setOpenRejectDialog(false)
  }
  const handleCloseRequestDialog = (): void => {
    setOpenRequestDocumentsDialog(false)
  }
  const handleRequestDocuments = (reason: string): void => {
    if (vendorId) {
      void dispatch(respondToVerification({ vendorId, status: VendorStatus.activated, note: reason }))
    }
    setOpenRequestDocumentsDialog(false)
  }
  const handleRejectVendor = (reason: string, months?: string): void => {
    if (vendorId) {
      void dispatch(
        respondToVerification({
          vendorId,
          status: VendorStatus.rejected,
          note: reason,
          retryDate: getDateRetryVerification(months),
        }),
      )
    }
    setOpenRejectDialog(false)
  }
  const handleVerifyVendor = (): void => {
    if (vendorId) {
      void dispatch(respondToVerification({ vendorId, status: VendorStatus.verified }))
    }
    setOpenAcceptDialog(false)
  }

  const handleDownload = async (
    fileKey: string,
    fileType: GeneratedFileType,
    index: number,
  ): Promise<void> => {
    if (fileType === GeneratedFileType.VendorRefDoc) {
      setLoadingRefDoc(index)
    } else {
      setLoadingOtherDoc(index)
    }
    if (fileKey) {
      try {
        const generatedFile = await getFileUrl({
          key: fileKey,
          mode: 'r',
        })
        await downloadFile(fileType, generatedFile)
      } catch (error) {
        console.error('Error while handling download:', error)
      } finally {
        setLoadingRefDoc(null)
        setLoadingOtherDoc(null)
      }
    }
  }

  const earliestValidTo = useMemo(() => {
    if (!vendor?.user?.certificates) {
      return null
    }
    return getValidityCertificateDate(vendor.user)
  }, [vendor?.user?.certificates])

  const isDatePassed = useIsDatePassed(earliestValidTo)

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Typography variant="title5" sx={{ color: palette.white.main }}>
          Dobrodošli na stranicu za VERIFIKACIJU vendora
        </Typography>
        <div className={styles.headerDescription}>
          <Typography
            variant="title5"
            sx={{ color: palette.gray.main, width: '75%', whiteSpace: 'pre-line' }}
          >
            U tabelama ispod možete pregledati sve informacije koje su Vam bitne da vam pomognu{'\n'}u odluci
            da li da nekog vendora verifikujete.{'\n'}U delu “Mehanizam za odlučivanje” možete da vidite koje
            parametre vendori prolaze, sa kojim parametrima su na granici a sa kojima su ispod margine.
            Ukoliko smatrate da ove informacije nisu dovoljne, uvek možete da zatražite dodatna dokumenta od
            Vendora da Vam pošalju i ta dokumenta možete naći u delu “Dodatna dokumenta”
          </Typography>
          <div className={styles.buttons}>
            <ThemeButton
              onClick={() => {
                setOpenAcceptDialog(true)
              }}
              sx={{
                backgroundColor: palette.charts.green200.main,
                '&:hover': {
                  backgroundColor: palette.charts.green200.main,
                },
              }}
            >
              VERIFIKUJ VENDORA
            </ThemeButton>
            <ThemeButton
              onClick={() => {
                setOpenRejectDialog(true)
              }}
              sx={{
                backgroundColor: palette.charts.indigo200.main,
                '&:hover': {
                  backgroundColor: palette.charts.indigo200.main,
                },
              }}
            >
              ODBIJ VENDORA
            </ThemeButton>
          </div>
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.left}>
          <div className={styles.decision}>
            <Typography variant="title1" sx={{ color: palette.white.main, paddingInline: '20px' }}>
              Mehanizam za odlučivanje
            </Typography>
            <Divider className={styles.divider} flexItem />
            <DecisionMechanism />
          </div>
          <div className={styles.documents}>
            <Typography variant="title1" sx={{ color: palette.white.main, paddingInline: '20px' }}>
              Dodatna dokumenta za verifikaciju
            </Typography>
            <Divider className={styles.divider} flexItem />
            <div className={styles.docContainer}>
              {vendor?.additionalDocuments ?? vendor?.status === VendorStatus.additional_documents ? (
                <div className={styles.additionalDocumentsContainer}>
                  <div className={styles.documentsRow}>
                    {vendor?.additionalDocuments?.documents.map((document: GeneratedFile, index: number) => (
                      <div key={document.key} className={`${styles.document} ${styles.refDocument}`}>
                        <div
                          className={styles.iconContainer}
                          onClick={() => {
                            void handleDownload(document.key, GeneratedFileType.VendorOtherDoc, index)
                          }}
                        >
                          {loadingOtherDoc === index ? (
                            <div className={styles.loader}>
                              <CircularProgress size={15} />
                            </div>
                          ) : (
                            <DownloadIcon height="16px" width="16px" fill={palette.blue100.main} />
                          )}
                        </div>
                        <div className={styles.documentContent}>
                          <Typography className={styles.text} variant="subtitle1">
                            {document.filename}
                          </Typography>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={styles.adminNote}>
                    {vendor?.status !== VendorStatus.pending_documents && (
                      <Typography
                        variant="title5"
                        sx={{
                          color: palette.gray.main,
                          width: '100%',
                          height: '50%',
                          textAlign: 'center',
                          paddingBlock: '20px',
                        }}
                      >
                        Dokumenta su zatražena i čeka se na dostavu dokumenata
                      </Typography>
                    )}
                    {vendor?.verificationNote && (
                      <div className={styles.bottomNote}>
                        <Typography
                          variant="title3"
                          sx={{
                            color: palette.white.main,
                            width: '100%',
                            textAlign: 'start',
                          }}
                        >
                          Vaš zahtev
                        </Typography>
                        <div className={styles.footer}>
                          <Typography
                            variant="title4"
                            sx={{ color: palette.white.main, width: '100%', textAlign: 'start' }}
                          >
                            {vendor.verificationNote}
                          </Typography>
                        </div>
                        {(vendor?.status === VendorStatus.pendingVerification ||
                          vendor?.status === VendorStatus.pending_documents) && (
                          <div className={styles.footerButton}>
                            <ThemeButton
                              onClick={() => {
                                setOpenRequestDocumentsDialog(true)
                              }}
                              sx={{
                                backgroundColor: palette.blue200.main,
                                '&:hover': {
                                  backgroundColor: palette.blue200.main,
                                },
                              }}
                            >
                              ZATRAŽI DODATNA DOKUMENTA
                            </ThemeButton>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                vendor?.status === VendorStatus.pendingVerification && (
                  <div className={styles.noDoc}>
                    <Typography
                      variant="title5"
                      sx={{
                        color: palette.gray.main,
                        width: '100%',
                        textAlign: 'center',
                        paddingBlock: '20px',
                        paddingTop: '56px',
                      }}
                    >
                      Trenutno nema nijednog zatraženog dokumenta, možete zatražiti maksimalno 4 dokumenta
                    </Typography>
                    <ThemeButton
                      onClick={() => {
                        setOpenRequestDocumentsDialog(true)
                      }}
                      sx={{
                        backgroundColor: palette.blue200.main,
                        '&:hover': {
                          backgroundColor: palette.blue200.main,
                        },
                      }}
                    >
                      ZATRAŽI DODATNA DOKUMENTA
                    </ThemeButton>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
        <div className={styles.right}>
          <div className={styles.vendor}>
            <InfoPanel />
          </div>
          {vendor?.user?.role !== Role.consultant ? (
            <div className={styles.refDocuments}>
              <Typography variant="title1" sx={{ color: palette.white.main, paddingInline: '20px' }}>
                Referentna dokumenta
              </Typography>
              <Divider className={styles.divider} flexItem />
              <div className={styles.refDocumentWrapper}>
                {vendor?.referenceDocuments && !!vendor?.referenceDocuments.documents.length ? (
                  vendor?.referenceDocuments.documents.map((document: GeneratedFile, index: number) => (
                    <div key={document.key} className={styles.document}>
                      <div
                        className={styles.iconContainer}
                        onClick={() => {
                          void handleDownload(document.key, GeneratedFileType.VendorRefDoc, index)
                        }}
                      >
                        {loadingRefDoc === index ? (
                          <div className={styles.loader}>
                            <CircularProgress size={15} />
                          </div>
                        ) : (
                          <DownloadIcon height="16px" width="16px" fill={palette.blue100.main} />
                        )}
                      </div>
                      <div className={styles.documentContent}>
                        <Typography className={styles.text} variant="subtitle1">
                          {document.filename}
                        </Typography>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.docNotExist}>
                    <Typography variant="title5" sx={{ color: palette.gray.main, textAlign: 'center' }}>
                      Ne postoji nijedan dokument
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.certificatesWrapper}>
              <Typography variant="title1" sx={{ color: palette.white.main, paddingInline: '20px' }}>
                Sertifikati
              </Typography>
              <Divider className={styles.divider} flexItem />
              {vendor?.user?.certificates && vendor?.user?.certificates.length > 0 ? (
                <div className={styles.certificatesContainer}>
                  <div className={styles.certificates}>
                    {[...Array(5)].map((_, index) => (
                      <div key={index} className={styles.certificate}>
                        {vendor?.user?.certificates && index < vendor?.user.certificates.length ? (
                          <SimpleTooltip
                            title={vendor?.user.certificates[index].name}
                            style={{ textAlign: 'left', height: 35, width: 40 }}
                          >
                            {getCertificateIcon(vendor?.user.certificates[index].slug, 35, 40)}
                          </SimpleTooltip>
                        ) : (
                          <></>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className={styles.dates}>
                    <div className={styles.dateContainer}>
                      <Typography variant="title4">Datum Važenja</Typography>
                      <div className={styles.dateLabel}>
                        <Typography
                          variant="title3"
                          style={{ color: isDatePassed ? palette.charts.red.main : palette.green.main }}
                        >
                          {earliestValidTo ? formatDate(earliestValidTo) : '-'}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.docNotExist}>
                  <Typography variant="title5" sx={{ color: palette.gray.main, textAlign: 'center' }}>
                    Ne postoji nijedan sertifikat
                  </Typography>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {openAcceptDialog && (
        <ThemeConfirmDialog
          onClose={handleCloseAcceptDialog}
          open={openAcceptDialog}
          title={'Verifikuj vendora?'}
          confirmButtonText={'VERIFIKUJ'}
          confirmButtonAction={handleVerifyVendor}
          declineButtonText={'IZAĐI'}
          description={
            'Pritiskom na dugme “Verifikuj” ovaj korisnik stiče pravo na korišćenje aplikacije i učestvovanje na tenderima.'
          }
        />
      )}
      {openRejectDialog && (
        <DeclineVerificationDialog
          onClose={handleCloseVerificationDialog}
          confirmButtonText={'ODBIJ'}
          declineButtonText={'IZAĐI'}
          title="Odbij zahtev za verifikaciju?"
          subtitle="Ukoliko želite da odbijete ovog vendora morate napisati razlog odbijanja i naznačiti datum kada vendor može ponovo da aplicira za verifikaciju."
          textInputName="Razlog odbijanja"
          displayMonths={true}
          open={openRejectDialog}
          confirmButtonAction={handleRejectVendor}
        />
      )}
      {openRequestDocumentsDialog && (
        <DeclineVerificationDialog
          onClose={handleCloseRequestDialog}
          className={styles.requestDialog}
          confirmButtonText={'POŠALJI'}
          declineButtonText={'IZAĐI'}
          title="Potrebna su Vam dodatna dokumenta za verifikaciju?"
          subtitle="Ukoliko Vam nije dovoljno informacija da biste verifikovali Vendora, možete mu zatražiti dodatna dokumenta kako bi bili sigurni. U polju ispod napišite kakva dokumenta su Vam potrebna da vendor pošalje."
          textInputName="Koja dokumenta su potrebna"
          open={openRequestDocumentsDialog}
          confirmButtonAction={handleRequestDocuments}
        />
      )}
    </div>
  )
}

export default VerificationProfile
